import classNames from "classnames";

import { mainNavIcons } from ".";
import {
	EPreviewTypes,
	IHomepageMainNavItems,
	ModuleMap,
	NavMainItems,
	PageModule,
	PageModuleType,
	Session,
	SessionTypesEnum,
} from "../../../../../types/working-model";
import Icon, { COLORS } from "../../../../general-ui/icon";
import MainEventBanner from "../../../../live-event/modules/main-event-banner/main-event-banner";
import About from "../../../../live-event/modules/about/about";
import Speakers from "../../../../live-event/modules/speakers/speakers";
import Agenda from "../../../../live-event/modules/agenda/agenda";
import CallOut from "../../../../live-event/modules/call-out/call-out";
import Sponsors from "../../../../live-event/modules/sponsors/sponsors";
import Products from "../../../../live-event/modules/products/products";
import Videos from "../../../../live-event/modules/video/videos";
import BlankSection from '../../homepage/editor/blank-section';
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortPostRegisterHomeModules } from "./home";
import './main-preview.scss';
import NewDropdown from "../../../../general-ui/dropdown/new-dropdown";
import { EIcon } from "../../../../general-ui/icon/icon";
import { Link, useParams } from "react-router-dom";
import { PATHNAMES } from "../../../admin";
import { ParamsProps } from "../../../../live-event/live-event";
import { useTranslation } from "react-i18next";
import HomePreviewModal from "../editor/preview/home-preview";
import SessionPreviewModal from "../editor/preview/session-preview";
import useTranslationModules from "../../../../../i18n/useTranslationModules";
import { useDispatch, useSelector } from "react-redux";
import { EventsState } from "../../../../../store/types";
import { resetScrollState } from "../../../../../store/actions/admin/event-panel";

// used to convert the SessionTypesEnum into the tabs used in the editor url
const editorSessionPageUrlTabs: { [key: string]: string } = {
	'onDemand': 'on-demand',
	'broadcast': 'event-agenda'
};

interface IMainPreviewProps {
	eventMainNavItems: IHomepageMainNavItems[];
}

interface IRenderLiveModulesProps {
	modules: PageModule[];
	template: TemplateClassNames
}

export const RenderLiveModules = React.forwardRef<HTMLDivElement, IRenderLiveModulesProps>(({ modules, template }, previewContainerRef: React.ForwardedRef<HTMLDivElement>) => {
	const {
		sessions,
		workingEvent
	} = useTypedSelector(state => ({
		sessions: state.CreateEventReducer.workingEvent?.sessions,
		workingEvent: state.CreateEventReducer.workingEvent
	}));

	const {
		EventPanelReducer: {
			scrollToModule,
		}
	} = useSelector((event: EventsState) => event);

	const dispatch = useDispatch();
	const moduleRefMap = useRef<any>({});

	const handleScroll = useCallback(async (type: PageModuleType) => {
		const moduleRef = moduleRefMap.current[type];
		if (previewContainerRef && moduleRef) {
			(previewContainerRef as MutableRefObject<HTMLDivElement>).current.scrollTop = moduleRef.offsetTop;
		}
	}, [previewContainerRef]);

	useEffect(() => {
		if (scrollToModule !== null) {
			handleScroll(scrollToModule);
		}
		// after triggering the scroll, reset state incase 
		// user clicks same module after scrolling
		setTimeout(() => dispatch(resetScrollState()), 2000);
	}, [dispatch, scrollToModule, handleScroll]);

	if (!modules?.length) return null;
	return (
		<div>
			{
				modules.map(module => {
					let moduleTitle = ModuleMap[module.type];
					if (module.content?.custom_heading?.changed === 'true') {
						moduleTitle = module.content?.custom_heading?.base || '';
					}

					if (!module.is_on || !template) return null;
					switch (module.type) {
						case PageModuleType.main_event_banner: return <MainEventBanner eventBundle={workingEvent} key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.about: return <About key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.speakers: return <Speakers key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.agenda: return <Agenda isLive={false} isUpNext={true} key={module.id} module={module} template={template} sessions={sessions || []} eventBundle={workingEvent} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.callout: return <CallOut key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.sponsors: return <Sponsors key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.products: return <Products key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.videos: return <Videos key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						case PageModuleType.blank: return <BlankSection key={module.id} page_module={module} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
						default:
							return <div key={module.id}>{moduleTitle}</div>;
					}
				})}
		</div>
	);
});

RenderLiveModules.displayName = "RenderLiveModules";

const MainPreview: React.FC<IMainPreviewProps> = ({
	eventMainNavItems,
}) => {

	const logo = useTypedSelector(state => state.CreateEventReducer?.workingEvent?.homepage?.header?.logo);
	const modules = useTypedSelector(state => state.CreateEventReducer.workingEvent?.homepage?.modules);
	const post_register_home_modules = useTypedSelector(state => state.CreateEventReducer.workingEvent?.homepage?.post_register_home_modules);
	const _template = useTypedSelector(state => state.CreateEventReducer.workingEvent?.template.name);

	const language = "en"; //locked to english for now because the post registered view does not currently have a language dropdown, when this changes, language will need to come from props

	useTranslationModules({ language });

	const sortedModules = useMemo(() => {
		if (!modules || !post_register_home_modules) return [];
		return sortPostRegisterHomeModules(modules, post_register_home_modules);
	}, [modules, post_register_home_modules]);

	const template = _template ? TemplateClassNames[_template] : TemplateClassNames.Apollo;

	return (
		<>
			<PostRegisterSideBar {...{ eventMainNavItems, template, logo }} activeMenuItem={NavMainItems.Home} />
			<section className="post-register-main-body">
				<RenderLiveModules modules={sortedModules} template={template} />
			</section>
		</>
	);
};

export default MainPreview;

interface IPostRegisterSideBarProps {
	eventMainNavItems: IHomepageMainNavItems[];
	template: TemplateClassNames;
	activeMenuItem: string;
	logo?: string | null;
}

export const PostRegisterSideBar: React.FC<IPostRegisterSideBarProps> = (props) => {
	// activeMenutItem here is now a prop rather than a redux state
	const { eventMainNavItems, template, activeMenuItem, logo } = props;

	const {
		editorSize,
		workingEvent
	} = useTypedSelector(state => ({
		editorSize: state.CreateEventReducer.editorSize,
		workingEvent: state.CreateEventReducer.workingEvent
	}));
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);

	const [openTabletMenu, setOpenTabletMenu] = useState(false);

	const { language }: ParamsProps = useParams();
	const { t } = useTranslation('homepage');

	function renderNavItems(isDesktop: boolean) {
		// get session page tab. 
		let sessionTab: string | undefined;
		const eventSessionTypes = new Set<SessionTypesEnum>();
		workingEvent?.sessions.forEach((session: Session) => eventSessionTypes.add(session.session_type));
		// If broadcast session exists, go there. Otherwise use order of the SessionTypesEnum
		if (eventSessionTypes.has(SessionTypesEnum.broadcast)) {
			sessionTab = editorSessionPageUrlTabs.broadcast;
		} else {
			for (const sessionType in SessionTypesEnum) {
				if (eventSessionTypes.has(SessionTypesEnum[sessionType as keyof typeof SessionTypesEnum])) {
					sessionTab = editorSessionPageUrlTabs[sessionType];
					break;
				}
			}
		}

		return eventMainNavItems.map(item => {
			const icon = mainNavIcons(item.name);
			const navPath = item.name !== NavMainItems.Session
				? PATHNAMES.Event[item.name](workingEvent!.uuid, sessionTab ?? '')
				: PATHNAMES.Session.SessionLink(workingEvent!.uuid, workingEvent!.sessions[0].uuid, workingEvent!.sessions[0].default_language);

			const isSingleSession = workingEvent?.sessions.length === 1;
			const active = activeMenuItem
				// if activeMenuItem isnt an empty string, 
				// check which tab is active
				? activeMenuItem.toUpperCase() === item.name.toUpperCase()
				// if isSingleSession and active menu is empty, 
				// session(s) tab should be active
				: isSingleSession ? (item.name === NavMainItems.Session) : item.name === NavMainItems.Home;

			let name: any;
			let useTranslate = false;
			if (language && !item.custom_name[language] && ["Home", "Agenda", "Sessions"].includes(item.custom_name.base)) {
				const base = item.custom_name.base as "Home" | "Agenda" | "Sessions";
				name = t(base);
				useTranslate = true;
			}

			if (!item.is_on) return null;
			return (
				<li key={item.name} className="side-nav-link">
					<Link to={navPath} className={classNames({ active: active })}>
						{isDesktop && icon ? (
							<Icon name={icon} color={COLORS.WHITE} size={24} />
						) : null}
						<h6>{useTranslate ? name : (item.custom_name[language] ?? item.custom_name.base)}</h6>
					</Link>
				</li >

			);
		});
	}

	const isDesktop = editorSize === 'desktop';
	return (
		<div className={classNames("post-register-side-bar", template, editorSize, { sessions: activeMenuItem === NavMainItems.Sessions })}>
			<div className="post-register-preview-logo">
				{logo ? (
					<img
						className="logo"
						alt="Logo"
						src={logo}
					/>
				) : (
					<h3>
						Your Logo
					</h3>
				)}
			</div>
			{isDesktop && (
				<ul className="main-nav-container">
					{renderNavItems(isDesktop)}
				</ul>)
			}
			{
				!isDesktop && (
					<>
						<div className={classNames("dropdown-background", { open: openTabletMenu })} />
						<NewDropdown
							className="menu-tablet"
							popupClassName="menu-tablet-popup"
							showArrow={false}
							renderTitle={({ isOpen }) => {
								setOpenTabletMenu(isOpen);
								return isOpen ? (
									<EIcon name="close" size="large" />
								) : (
									<EIcon name="menu" size="large" />
								);
							}}
						>
							<div className="main-nav-container">
								{renderNavItems(isDesktop)}
							</div>
						</NewDropdown>
					</>
				)
			}
			<HomePreviewModal open={previewMode === EPreviewTypes.Home} />
			<SessionPreviewModal open={previewMode === EPreviewTypes.Session} singleSession={workingEvent?.sessions.length === 1} />
		</div>
	);
};
